<template>
  <section>
    <Accordion
      type="multiple"
      :default-value="['basic', 'financial', 'recurring']"
      class="tw-w-full"
    >
      <!-- Basic Information -->
      <AccordionItem value="basic" :class="sectionClass">
        <AccordionTrigger :class="triggerClass">
          <span class="tw-flex tw-items-center tw-gap-2.5">
            <Info :class="iconClass" />
            <span :class="labelClass">{{ t('orders.titles.basic_info') }}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <dl class="tw-pb-3">
            <DetailRow
              :label="t('orders.titles.order_no')"
              :value="order?.recurring_number || '-'"
            />
            <DetailRow
              :label="t('orders.titles.customer')"
              :value="order?.customer_name"
              :to="
                order?.customer
                  ? { name: 'view-customer', params: { id: order.customer } }
                  : undefined
              "
            />
            <DetailRow
              :label="t('requests.titles.project')"
              :value="order?.project_name || '-'"
              :to="
                order?.project ? { name: 'edit-project', params: { id: order.project } } : undefined
              "
            />
            <DetailRow
              :label="t('orders.titles.supplier')"
              :value="order?.supplier_name || '-'"
              :to="
                order?.supplier
                  ? { name: 'edit-supplier', params: { supplierId: order.supplier } }
                  : undefined
              "
            />
            <DetailRow
              :label="t('orders.titles.supplier_no')"
              :value="order?.supplier_number || '-'"
            />
            <DetailRow
              :label="t('orders.titles.created_by')"
              :value="order?.created_by_name || '-'"
            />
          </dl>
        </AccordionContent>
      </AccordionItem>

      <!-- Financial Information -->
      <AccordionItem value="financial" :class="sectionClass">
        <AccordionTrigger :class="triggerClass">
          <span class="tw-flex tw-items-center tw-gap-2.5">
            <Wallet :class="iconClass" />
            <span :class="labelClass">{{ t('orders.titles.financial_info') }}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <dl class="tw-pb-3">
            <DetailRow
              v-if="order?.net_eur !== null"
              :label="t('orders.titles.amount_net')"
              :value="order?.net_eur ? n(+order.net_eur, 'currency') : '-'"
            />
            <DetailRow :label="t('orders.titles.amount_gross')" :value="order?.amount || '-'" />
            <DetailRow :label="t('orders.titles.remaining_budget')" value="-" />
            <DetailRow :label="t('orders.titles.remaining_balance')" value="-" />
          </dl>
        </AccordionContent>
      </AccordionItem>

      <!-- Recurring Information -->
      <AccordionItem value="recurring" :class="sectionClass">
        <AccordionTrigger :class="triggerClass">
          <span class="tw-flex tw-items-center tw-gap-2.5">
            <RefreshCw :class="iconClass" />
            <span :class="labelClass">{{ t('orders.recurring.recurring_info') }}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <dl class="tw-pb-3">
            <DetailRow :label="t('orders.titles.order_name')" :value="order?.name || '-'" />
            <DetailRow :label="t('orders.recurring.frequency')" :value="order?.frequency || '-'" />
            <DetailRow
              :label="t('orders.recurring.ends_after')"
              :value="order?.end_after !== null ? String(order.end_after) : '0'"
            />
          </dl>
        </AccordionContent>
      </AccordionItem>

      <!-- Dates -->
      <AccordionItem value="dates" :class="sectionClass">
        <AccordionTrigger :class="triggerClass">
          <span class="tw-flex tw-items-center tw-gap-2.5">
            <Calendar :class="iconClass" />
            <span :class="labelClass">{{ t('orders.titles.dates') }}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <dl class="tw-pb-3">
            <DetailRow
              :label="t('orders.titles.created_on')"
              :value="moment(order?.created_on).format('DD.MM.YYYY')"
            />
            <DetailRow
              :label="t('orders.titles.service_date')"
              :value="moment(order?.service_date).format('DD.MM.YYYY')"
            />
            <DetailRow
              v-if="order?.date_order_list !== null"
              :label="t('orders.titles.date_order_list')"
              :value="moment(order?.date_order_list).format('DD.MM.YYYY')"
            />
            <DetailRow
              v-if="order?.order_triggered_on !== null"
              :label="t('orders.titles.triggered_on')"
              :value="moment(order?.order_triggered_on).format('DD.MM.YYYY')"
            />
            <DetailRow
              :label="t('orders.titles.expected_processing_date')"
              :value="order?.expected_date_processing || '-'"
            />
          </dl>
        </AccordionContent>
      </AccordionItem>

      <!-- Additional Details -->
      <AccordionItem v-if="hasAdditionalDetails" value="additional" :class="sectionClass">
        <AccordionTrigger :class="triggerClass">
          <span class="tw-flex tw-items-center tw-gap-2.5">
            <FileText :class="iconClass" />
            <span :class="labelClass">{{ t('orders.titles.additional_details') }}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <dl class="tw-pb-3">
            <DetailRow
              :label="t('orders.titles.doc_no_internal')"
              :value="order?.document_no_internal || '-'"
            />
            <DetailRow :label="t('orders.titles.project_no')" :value="order?.project_no || '-'" />
            <DetailRow
              :label="t('orders.titles.supplier_order_no')"
              :value="order?.order_no_supplier || '-'"
            />
            <DetailRow
              v-if="order?.preliminary_insolvency_proceedings !== null"
              :label="t('orders.titles.prem_insv_proc')"
              :value="
                order?.preliminary_insolvency_proceedings
                  ? t('common.labels.yes')
                  : t('common.labels.no')
              "
            />
          </dl>
        </AccordionContent>
      </AccordionItem>

      <!-- Comments Section -->
      <AccordionItem v-if="hasComments" value="comments" :class="sectionClass">
        <AccordionTrigger :class="triggerClass">
          <span class="tw-flex tw-items-center tw-gap-2.5">
            <MessageSquareText :class="iconClass" />
            <span :class="labelClass">{{ t('orders.titles.order_comments') }}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <dl class="tw-pb-3">
            <DetailRow
              v-if="order?.comment !== null"
              :label="t('orders.titles.comment')"
              :value="order?.comment || '-'"
              :multiline="true"
            />
            <DetailRow
              v-if="order?.comment_management !== null"
              :label="t('orders.titles.comment_management')"
              :value="order?.comment_management || '-'"
              :multiline="true"
            />
            <DetailRow
              v-if="order?.comment_insv !== null"
              :label="t('orders.titles.comment_insv')"
              :value="order?.comment_insv || '-'"
              :multiline="true"
            />
          </dl>
        </AccordionContent>
      </AccordionItem>

      <!-- Approval Information -->
      <AccordionItem
        v-if="hasApprovalInfo"
        value="approval"
        :class="sectionClass + ' last:tw-border-b-0'"
      >
        <AccordionTrigger :class="triggerClass">
          <span class="tw-flex tw-items-center tw-gap-2.5">
            <ShieldCheck :class="iconClass" />
            <span :class="labelClass">{{ t('orders.titles.approval_info') }}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <dl class="tw-pb-3">
            <DetailRow
              v-if="order?.status === 'APPROVED' && order?.approved_by?.display_name"
              :label="t('orders.titles.approved_by')"
              :value="order.approved_by.display_name"
            />
            <DetailRow
              v-if="order?.status === 'REJECTED' && order?.rejected_by?.display_name"
              :label="t('orders.titles.rejected_by')"
              :value="order.rejected_by.display_name"
            />
            <div v-if="order?.cleared_by?.length" class="tw-flex tw-flex-col tw-gap-2 tw-pt-2.5">
              <span class="tw-text-sm tw-text-surface-500 dark:tw-text-neutral-400">
                {{ t('orders.titles.cleared_by') }}
              </span>
              <div class="tw-flex tw-flex-wrap tw-gap-2">
                <Badge
                  v-for="(clearer, index) in order.cleared_by"
                  :key="index"
                  variant="secondary"
                  class="tw-text-xs"
                >
                  {{ clearer }}
                </Badge>
              </div>
            </div>
          </dl>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import moment from 'moment';
import { useI18n } from 'vue-i18n';
import {
  Calendar,
  FileText,
  Info,
  MessageSquareText,
  RefreshCw,
  ShieldCheck,
  Wallet,
} from 'lucide-vue-next';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { DetailRow } from '@/components/ui/detail-row';

const sectionClass =
  'tw-border-b tw-border-surface-200 dark:tw-border-neutral-800 last:tw-border-b-0';
const triggerClass =
  'tw-py-3.5 hover:tw-no-underline [&[data-state=open]>svg]:tw-text-secondary-700';
const iconClass = 'tw-h-[1.05rem] tw-w-[1.05rem] tw-text-surface-400 tw-shrink-0';
const labelClass =
  'tw-font-heading tw-text-sm tw-font-semibold tw-text-secondary-800 dark:tw-text-neutral-200';

const { t, n } = useI18n();

const props = defineProps({
  order: {
    type: Object,
    default: () => ({}),
  },
});

const hasAdditionalDetails = computed(() => {
  return (
    props.order?.document_no_internal ||
    props.order?.project_no ||
    props.order?.order_no_supplier ||
    props.order?.preliminary_insolvency_proceedings !== null
  );
});

const hasComments = computed(() => {
  return (
    props.order?.comment !== null ||
    props.order?.comment_management !== null ||
    props.order?.comment_insv !== null
  );
});

const hasApprovalInfo = computed(() => {
  return (
    props.order?.status === 'APPROVED' ||
    props.order?.status === 'REJECTED' ||
    (props.order?.cleared_by?.length ?? 0) > 0
  );
});
</script>
